import { type PaymentRequestData } from '../../lib/messaging-api';

const money = (n: number, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
};

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-[#fff7e6] text-[#9a6700] border-[#fde2a1]',
  PAID: 'bg-[#e7f6ec] text-[#1b7a3d] border-[#bfe6cb]',
  CANCELLED: 'bg-[#f3f3f3] text-[#908e8e] border-[#e5e5e5]',
  EXPIRED: 'bg-[#f3f3f3] text-[#908e8e] border-[#e5e5e5]',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Awaiting payment',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

/**
 * Renders a PAYMENT_REQUEST chat bubble. The payer (client) sees a
 * "Pay" button while PENDING; the creator (pro) sees a "Cancel" link.
 */
export function PaymentRequestBubble({
  pr,
  fromMe,
  canPay,
  onPay,
  onCancel,
  busy,
}: {
  pr: PaymentRequestData;
  // true if the current user created the request (the pro)
  fromMe: boolean;
  // true if the current user is the payer (the client)
  canPay: boolean;
  onPay?: () => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  // All-in gross the client pays (rate + fee + VAT). Falls back to the rate
  // if the gross hasn't been computed yet.
  const gross = pr.customer_amount ?? pr.amount;
  // What the pro actually nets (gross − fee − VAT). Falls back to the rate.
  const proNet = pr.pro_net_amount ?? pr.amount;
  const isPending = pr.status === 'PENDING';

  return (
    <div className="w-[260px] max-w-full rounded-2xl border border-[#ede8e9] bg-white text-[#3a3335] overflow-hidden">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-[#f3eee9]">
        <span className="material-icons text-[#8e2a2f] text-[20px]">
          request_quote
        </span>
        <span className="font-bold text-[13px]">Payment request</span>
        <span
          className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${
            STATUS_STYLE[pr.status] ?? STATUS_STYLE.CANCELLED
          }`}
        >
          {STATUS_LABEL[pr.status] ?? pr.status}
        </span>
      </div>

      <div className="px-4 py-3">
        <p className="text-[14px] font-medium break-words">{pr.description}</p>
        <p className="mt-1 font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[20px]">
          {money(Number(gross), pr.currency)}
        </p>

        {isPending && canPay && onPay ? (
          <button
            type="button"
            onClick={onPay}
            disabled={busy}
            className="mt-2 w-full h-[40px] rounded-[10px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
          >
            {busy ? 'Redirecting…' : `Pay ${money(Number(gross), pr.currency)}`}
          </button>
        ) : null}

        {isPending && fromMe && onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="mt-2 w-full h-[36px] rounded-[10px] border border-[#ede8e9] text-[#908e8e] text-[13px] font-bold hover:border-[#dc2626] hover:text-[#dc2626] transition-colors disabled:opacity-60"
          >
            {busy ? '…' : 'Cancel request'}
          </button>
        ) : null}

        {isPending && fromMe ? (
          <p className="mt-2 text-[11px] text-[#908e8e]">
            You'll receive {money(Number(proNet), pr.currency)} once paid.
          </p>
        ) : null}
      </div>
    </div>
  );
}
